/* eslint-disable @next/next/no-img-element */
// import useNativeLazyLoading from '@charlietango/use-native-lazy-loading';
import { FC, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

import { Box } from '@versumstudios/ui';

import * as Styles from './image.styles';

export interface IImageRenderer {
  children?: ReactNode;
  inDetailView: boolean;
  src: string;
  width: number;
  height: number;
  alt: string;
  sizes?: string;
  srcset?: string;
  square?: boolean;
  explicit?: boolean;
  allowsExplicit?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  allowsPhotosensitive?: boolean;
}

// Note: Native lazy loading isn't 100% reliable.
// it loads all images even outside the viewport.

export const ImageRenderer: FC<IImageRenderer> = ({
  children,
  inDetailView,
  src,
  width,
  height,
  alt,
  sizes,
  srcset,
  square,
  explicit,
  allowsExplicit,
}) => {
  // const supportsLazyLoading = useNativeLazyLoading();
  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px',
    // skip: supportsLazyLoading !== false,
  });

  // constrain height on weird ratios 100x12000
  const constrain = !inDetailView && width / height < 0.2;
  const smol = width + height <= 256;
  const blur = explicit && !allowsExplicit;

  // if on market tabs of collections render image as 1x1
  if (square) {
    return (
      <Box
        css={{
          position: 'relative',
          backgroundColor: '$secondaryBackground',
          overflow: 'hidden',
          '--aspect-ratio': 1,

          img: {
            position: 'absolute',
            top: 0,
            left: 0,
            objectFit: 'cover',
            objectPosition: 'center',
            width: '100%',
            height: '100%',
            flexGrow: 0,
          },

          svg: {
            position: 'absolute',
            bottom: '$4',
            right: '$4',
          },

          '&:before': {
            content: "''",
            display: 'block',
            width: '1px',
            height: 0,
            paddingBottom: 'calc(100% / (var(--aspect-ratio)))',
          },
        }}
      >
        <img
          sizes={sizes}
          srcSet={srcset}
          src={src}
          width={width}
          height={height}
          alt={alt}
          style={{ ...(blur && { filter: 'blur(10px)' }) }}
        />
        {children}
      </Box>
    );
  }

  if (inDetailView) {
    return (
      <Styles.Container
        css={{
          '--aspect-ratio': `${width / height}`,
          overflow: 'hidden',
          ...(constrain && { maxHeight: width * 1, overflow: 'hidden' }), // constrain
          ...(smol && {
            width: '100vw',
            height: '100vw',
            '@bp1': { width: '50vh', height: '50vh' },
            img: { width: '100%', height: `calc(100% * ${height / width})` },
          }), // smol images on detail view
        }}
        detail={inDetailView}
        smol={smol}
      >
        <img
          sizes={sizes}
          srcSet={srcset}
          src={src}
          width={width}
          height={height}
          alt={alt}
          style={{ ...(blur && { filter: 'blur(10px)' }) }}
        />
        {children}
      </Styles.Container>
    );
  }

  return (
    <Styles.Container
      ref={ref}
      css={{
        '--aspect-ratio': `${width / height}`,
        overflow: 'hidden',
        ...(constrain && { maxHeight: width * 1, overflow: 'hidden' }),
      }}
      detail={inDetailView}
      smol={smol}
    >
      {inView && (
        <img
          sizes={sizes}
          srcSet={srcset}
          src={src}
          width={width}
          height={height}
          alt={alt}
          style={{ ...(blur && { filter: 'blur(10px)' }) }}
        />
      )}
      {children}
    </Styles.Container>
  );
};
